/**
 * Голосования — разметка 1:1 с legacy htdocs/core/templates/admin_votes.tpl
 * (список: .infobox, .article/.title/.ctrl, чекбокс .is_online, кнопка удаления
 * .bt.red.delinform) и admin_votes_edit.tpl (форма: .form-control.news-input,
 * .questionlis/.questionitem/.voteinptblock, .detailstat, «Итоги голосования»).
 *
 * Legacy-режимы страницы /admin/votes/{id} воспроизведены как внутреннее состояние:
 *   список (id = -1) → создание (id = 0) → просмотр существующего (id > 0).
 *
 * ПАРИТЕТ (важно): в legacy у СУЩЕСТВУЮЩЕГО голосования формы нет вообще —
 * admin_votes_edit.tpl оборачивает <form> и кнопку «Сохранить» в
 * {if (!isset($data.material.votes_results))}, а get_vote() всегда возвращает ключ
 * votes_results. Поэтому открытое голосование показывается только на просмотр
 * (с итогами), а поля названия и дат блокируются при count(votes_results) > 1.
 * Копируем как есть (CLAUDE.md п.6) — см. notes.
 *
 * API: GET/POST /api/admin/content/votes, GET/DELETE /api/admin/content/votes/{id},
 * POST /api/admin/content/votes/{id}/online (Handlers\Admin\Content).
 * Bootstrap-модалка подтверждения заменена на window.confirm с теми же текстами.
 */

import { Fragment, useEffect, useState } from 'react'

import { api, ApiError } from '@/api/client'
import { isTrue } from '@/lib/format'
import { useLegacyCss } from '@/lib/legacyAssets'
import { useApi } from '@/lib/useApi'

/** Строка списка голосований (votes_list + count вопросов). */
interface VoteRow {
  id: number
  name: string
  date_start: string
  date_end: string
  date_create: string
  is_online: boolean | 't' | 'f'
  ca: number
}

/** Ответ GET /api/admin/content/votes. */
interface VotesListResponse {
  news: VoteRow[]
  countvot: Record<string, string> // «3 из 50»
  countvotnbv: Record<string, string> // legacy-баг: тот же массив, что и countvot
}

/** Вопрос голосования (votes_question). */
interface VoteQuestionRow {
  id: number
  vote_id: number
  question: string
  question_type: string
}

/** Результат по вопросу: ключ — «За»/«Против»/«Воздержался»/«Не голосовал». */
interface VoteResultCase {
  vote_qid: number
  question: string
  case: string
  names: string | null
}

/** Ответ GET /api/admin/content/votes/{id} (legacy Settings_model::get_vote). */
interface VoteDetails {
  votes_list: {
    id: number
    name: string
    date_start: string // Y-m-d
    date_end: string // Y-m-d
    date_create: string
    is_online: boolean | 't' | 'f'
  }
  votes_question: VoteQuestionRow[]
  votes_results: Record<string, Record<string, VoteResultCase>>
  votes_itog: Record<string, Record<string, { count: number | string; question: string }>>
}

/** «2024-03-15» → «15.03.2024» (legacy date('d.m.Y', strtotime(...))). */
function ddmmyyyy(value: string | null): string {
  if (value === null || value === '') return ''
  const [year, month, day] = value.slice(0, 10).split('-')
  return `${day}.${month}.${year}`
}

/** Сегодня в формате Y-m-d — для сравнений «Завершено» / «Планируется». */
function todayIso(): string {
  const now = new Date()
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`
}

export function AdminVotesPage() {
  useLegacyCss('admin', 'newapproach')

  const { data, reload } = useApi<VotesListResponse>('/api/admin/content/votes')

  /** null — список; 0 — новое голосование; иначе id открытого (legacy /admin/votes/{id}). */
  const [openId, setOpenId] = useState<number | null>(null)
  const [ok, setOk] = useState<string[]>([])
  const [error, setError] = useState<string[]>([])

  const votes = data?.news ?? []
  const countvot = data?.countvot ?? {}
  const countvotnbv = data?.countvotnbv ?? {}

  /** legacy: чекбокс is_online шлёт AJAX сразу при переключении. */
  const toggleOnline = async (id: number, isOnline: boolean): Promise<void> => {
    try {
      await api.post(`/api/admin/content/votes/${id}/online`, { is_online: isOnline })
      reload()
    } catch (e: unknown) {
      setError([e instanceof ApiError ? e.message : 'Ошибка при публикации голосования'])
    }
  }

  /**
   * legacy news-delete[id]: подтверждение «Удалить выбранное голосование?»,
   * а если по голосованию уже голосовали (data-haveans) — второе подтверждение.
   */
  const remove = async (id: number): Promise<void> => {
    if (!window.confirm('Удалить выбранное голосование?')) return

    // legacy: data-haveans проставлялся при countvotnbv[id] > 0 (строка «N из M» → N)
    const voted = Number.parseInt(countvotnbv[String(id)] ?? '0', 10)
    if (voted > 0) {
      const confirmed = window.confirm(
        ' ВНИМАНИЕ! По данному голосованию уже есть проголосовавшие! Подтверждаете удаление? ',
      )
      if (!confirmed) return
    }

    try {
      const result = await api.delete<{ message: string }>(`/api/admin/content/votes/${id}`)
      setOk([result.message])
      setError([])
      reload()
    } catch (e: unknown) {
      setError([e instanceof ApiError ? e.message : 'Ошибка при удалении голосования'])
    }
  }

  const infobox = (
    <>
      {error.map((message, index) => (
        <Fragment key={`e${index}`}>
          <p className="okke" style={{ color: '#ce4844', float: 'left' }}>
            {message}
          </p>
          <br />
        </Fragment>
      ))}
      {ok.map((message, index) => (
        <Fragment key={`o${index}`}>
          <p className="okke" style={{ float: 'left' }}>
            {message}
          </p>
          <br />
        </Fragment>
      ))}
    </>
  )

  if (openId !== null) {
    return (
      <VoteEditor
        id={openId}
        onBack={() => {
          setOpenId(null)
          reload()
        }}
      />
    )
  }

  const today = todayIso()

  // ── Список голосований (legacy admin_votes.tpl) ──────────────────────────
  return (
    <>
      <div className="infobox clearfix">
        {infobox}
        <div style={{ float: 'right' }}>
          <a
            className="bt green"
            href="#"
            onClick={(e) => {
              e.preventDefault()
              setOpenId(0)
            }}
          >
            добавить голосование
          </a>
        </div>
      </div>

      <form className="form-news" method="POST" onSubmit={(e) => e.preventDefault()}>
        {votes.map((vote) => (
          <Fragment key={vote.id}>
            <hr />
            <div className="article">
              <div className="title">
                <label style={{ cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    className="is_online"
                    name={`is_online[${vote.id}]`}
                    value={vote.id}
                    checked={isTrue(vote.is_online)}
                    onChange={(e) => void toggleOnline(vote.id, e.target.checked)}
                  />
                </label>
                {vote.name}{' '}
                <span style={{ fontSize: '13px', color: '#737373' }}>
                  (с {ddmmyyyy(vote.date_start)} по {ddmmyyyy(vote.date_end)})
                </span>
                {vote.date_end.slice(0, 10) < today && (
                  <span
                    style={{ fontSize: '13px', color: '#dc0000', textTransform: 'uppercase' }}
                  >
                    {' '}
                    Завершено
                  </span>
                )}
                {vote.date_start.slice(0, 10) > today && (
                  <span style={{ fontSize: '13px', color: '#737373' }}> Планируется</span>
                )}
                <br />
                <span style={{ fontSize: '13px', color: '#737373' }}>
                  Дата создания: {ddmmyyyy(vote.date_create)}
                </span>
                <br />
                <span style={{ fontSize: '13px', color: '#737373' }}>
                  Количество пунктов (вопросов): {vote.ca}
                </span>
                <br />
                <span style={{ fontSize: '13px', color: '#737373' }}>
                  Проголосовало человек: {countvot[String(vote.id)] ?? ''}
                </span>
                <br />
              </div>
              <div className="ctrl">
                <a
                  href="#"
                  className="bt orange"
                  onClick={(e) => {
                    e.preventDefault()
                    setOpenId(vote.id)
                  }}
                >
                  редактировать
                </a>
                <input
                  type="submit"
                  name={`news-delete[${vote.id}]`}
                  className="bt red delinform"
                  value="удалить"
                  data-confirm="Удалить выбранное голосование?"
                  onClick={(e) => {
                    e.preventDefault()
                    void remove(vote.id)
                  }}
                />
              </div>
            </div>
          </Fragment>
        ))}
      </form>
    </>
  )
}

// ─── Создание / просмотр голосования (legacy admin_votes_edit.tpl) ───────────

function VoteEditor({ id, onBack }: { id: number; onBack: () => void }) {
  const isNew = id === 0

  const { data: details } = useApi<VoteDetails>(isNew ? null : `/api/admin/content/votes/${id}`)

  const [title, setTitle] = useState('')
  const [dateStart, setDateStart] = useState(() => todayIso())
  const [dateEnd, setDateEnd] = useState(() => {
    const week = new Date(Date.now() + 7 * 24 * 3600 * 1000)
    return `${week.getFullYear()}-${String(week.getMonth() + 1).padStart(2, '0')}-${String(week.getDate()).padStart(2, '0')}`
  })
  const [isCandy, setIsCandy] = useState(false)
  const [questions, setQuestions] = useState<string[]>([''])
  const [ok, setOk] = useState<string[]>([])
  const [error, setError] = useState<string[]>([])

  useEffect(() => {
    if (details === null) return
    setTitle(details.votes_list.name)
    setDateStart(details.votes_list.date_start)
    setDateEnd(details.votes_list.date_end)
  }, [details])

  const results = details?.votes_results ?? {}
  // legacy: у существующего голосования votes_results всегда установлен → формы нет
  const readOnly = !isNew
  const disabled = readOnly && Object.keys(results).length > 1

  /** legacy news-submit (только для нового голосования). */
  const submit = async (): Promise<void> => {
    try {
      const result = await api.post<{ id: number; message: string }>('/api/admin/content/votes', {
        title,
        date_start: dateStart,
        date_end: dateEnd,
        is_online: false,
        is_candy: isCandy,
        question: isCandy ? [] : questions,
      })
      setOk([result.message])
      setError([])
      onBack()
    } catch (e: unknown) {
      setError([e instanceof ApiError ? e.message : 'Ошибка при сохранении голосования'])
      setOk([])
    }
  }

  /** legacy .delquestionitem: подтверждение и удаление строки вопроса из формы. */
  const removeQuestion = (index: number): void => {
    if (!window.confirm('Удалить выбранный вопрос?')) return
    setQuestions((current) => current.filter((_, i) => i !== index))
  }

  const infobox = (
    <div className="infobox clearfix">
      {error.map((message, index) => (
        <Fragment key={`e${index}`}>
          <p className="okke" style={{ color: '#ce4844', float: 'left' }}>
            {message}
          </p>
          <br />
        </Fragment>
      ))}
      {ok.map((message, index) => (
        <Fragment key={`o${index}`}>
          <p className="okke" style={{ float: 'left' }}>
            {message}
          </p>
          <br />
        </Fragment>
      ))}
    </div>
  )

  /** Шапка: кнопка сохранения (только у нового) и ссылка «К списку голосований». */
  const head = (
    <div className="infobox clearfix" style={{ marginLeft: '0px' }}>
      {!readOnly && (
        <div style={{ float: 'left' }}>
          <input
            type="submit"
            name="news-submit"
            className="bt green"
            value={title !== '' ? 'Сохранить голосование' : 'Добавить голосование'}
          />
        </div>
      )}
      <div style={{ float: 'right' }}>
        <a
          className="bt"
          href="#"
          onClick={(e) => {
            e.preventDefault()
            onBack()
          }}
        >
          К списку голосований
        </a>
      </div>
    </div>
  )

  const fields = (
    <>
      <div className="form-control news-input" style={{ paddingBottom: '6px', display: 'table' }}>
        <span style={{ width: '150px', display: 'table-cell', color: '#999' }}>
          Название голосования:&nbsp;
        </span>
        <input
          name="title"
          style={{ display: 'table-cell', border: 0, outline: 0, width: '100%' }}
          value={title}
          disabled={disabled}
          onChange={(e) => setTitle(e.target.value)}
        />
      </div>
      <div className="form-control news-input" style={{ paddingBottom: '6px', display: 'table' }}>
        <span style={{ width: '150px', display: 'table-cell', color: '#999' }}>
          Начало голосования:&nbsp;
        </span>
        <input
          name="date_start"
          type="date"
          style={{ display: 'table-cell', border: 0, outline: 0, width: '100%' }}
          value={dateStart}
          disabled={disabled}
          onChange={(e) => setDateStart(e.target.value)}
        />
      </div>
      <div className="form-control news-input" style={{ paddingBottom: '6px', display: 'table' }}>
        <span style={{ width: '150px', display: 'table-cell', color: '#999' }}>
          Конец голосования:&nbsp;
        </span>
        <input
          name="date_end"
          type="date"
          style={{ display: 'table-cell', border: 0, outline: 0, width: '100%' }}
          value={dateEnd}
          disabled={disabled}
          onChange={(e) => setDateEnd(e.target.value)}
        />
      </div>

      <br />

      {/* legacy: чекбокс «за кандидатов» только у нового голосования */}
      {!readOnly && (
        <>
          <label style={{ cursor: 'pointer' }}>
            <input
              type="checkbox"
              id="for_candy"
              name="is_candy"
              checked={isCandy}
              onChange={(e) => setIsCandy(e.target.checked)}
            />{' '}
            Голосование за кандидатов
          </label>
          <br />
          <br />
        </>
      )}
    </>
  )

  // ── Просмотр существующего голосования (формы нет — паритет с legacy) ────
  if (readOnly) {
    const itog = details?.votes_itog ?? {}

    return (
      <>
        {infobox}
        {head}
        {fields}

        <div className="questionlis">
          {(details?.votes_question ?? []).map((question) => {
            const byCase = results[question.question] ?? {}
            const stat = (name: string): { count: number; names: string } => {
              const item = byCase[name]
              const names = item?.names ?? null
              if (names === null || names === '') return { count: 0, names: '...' }
              return { count: names.split(',').length, names }
            }
            const za = stat('За')
            const protiv = stat('Против')
            const vozd = stat('Воздержался')

            return (
              <div className="questionitem" key={question.id}>
                <div className="voteinptblock">
                  <div className="form-group nowrap">
                    <label style={{ fontWeight: 500 }}>
                      Вопрос <span className="votitr" />
                      <input
                        type="text"
                        name={`question[${question.id}]`}
                        className="form-control"
                        style={{ width: '400px' }}
                        value={question.question}
                        disabled={disabled}
                        readOnly
                      />
                    </label>
                  </div>
                  <div className="detailstat">
                    <table>
                      <tbody>
                        <tr>
                          <td>&quot;За&quot; ({za.count})</td>
                          <td>
                            {za.count > 0 && 'Уч. №№: '}
                            {za.names}
                          </td>
                        </tr>
                        <tr>
                          <td>&quot;Против&quot; ({protiv.count})</td>
                          <td>
                            {protiv.count > 0 && 'Уч. №№: '}
                            {protiv.names}
                          </td>
                        </tr>
                        <tr>
                          <td style={{ whiteSpace: 'nowrap', paddingRight: '10px' }}>
                            &quot;Воздержался&quot; ({vozd.count})
                          </td>
                          <td>
                            {vozd.count > 0 && 'Уч. №№: '}
                            {vozd.names}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )
          })}
        </div>

        <h3 style={{ paddingTop: '20px', paddingBottom: '5px' }}>Итоги голосования:</h3>

        {Object.keys(results).length >= 1 && (
          <div className="row">
            <div className="col-sm-6">
              <div className="voteza">
                {itog['За'] !== undefined && (
                  <>
                    Проголосовали <strong>&quot;За&quot;:</strong> <br />
                    <table className="table tdbottomborder">
                      <tbody>
                        {Object.entries(itog['За']).map(([qid, item]) => (
                          <tr key={qid}>
                            <td style={{ paddingRight: '10px', whiteSpace: 'nowrap' }}>
                              {item.count} чел.
                            </td>
                            <td>{item.question}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </>
                )}
              </div>
            </div>
            <div className="col-sm-6">
              <div className="voteprotiv">
                {itog['Против'] !== undefined && (
                  <>
                    Проголосовали <strong>&quot;Против&quot;:</strong> <br />
                    <table className="table tdbottomborder">
                      <tbody>
                        {Object.entries(itog['Против']).map(([qid, item]) => (
                          <tr key={qid}>
                            <td style={{ paddingRight: '10px', whiteSpace: 'nowrap' }}>
                              {item.count} чел.
                            </td>
                            <td>{item.question}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </>
    )
  }

  // ── Новое голосование (legacy id = 0) ────────────────────────────────────
  return (
    <>
      {infobox}

      <form
        className="add-form"
        method="POST"
        onSubmit={(e) => {
          e.preventDefault()
          void submit()
        }}
      >
        {head}
        {fields}

        {/* legacy: при «голосовании за кандидатов» список вопросов скрывается */}
        <div className="questionlis" style={{ display: isCandy ? 'none' : 'block' }}>
          {questions.map((question, index) => (
            <div className="questionitem" key={index}>
              <div className="voteinptblock">
                <div className="form-group">
                  <label style={{ fontWeight: 500 }}>
                    Вопрос <span className="votitr">{index + 1}</span>
                    <input
                      type="text"
                      name="question[]"
                      className="form-control"
                      style={{ width: '400px' }}
                      value={question}
                      onChange={(e) =>
                        setQuestions((current) =>
                          current.map((item, i) => (i === index ? e.target.value : item)),
                        )
                      }
                    />
                  </label>
                  {questions.length > 1 && (
                    <button
                      className="delquestionitem bt orange bt_rmva"
                      data-confirmdel="Удалить выбранный вопрос?"
                      onClick={(e) => {
                        e.preventDefault()
                        removeQuestion(index)
                      }}
                    >
                      Удалить
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div style={{ display: 'flex' }}>
          <input
            type="submit"
            name="news-submit"
            className="bt green"
            value={title !== '' ? 'Сохранить голосование' : 'Добавить голосование'}
          />
          <div className="addquestion" style={{ display: isCandy ? 'none' : 'block' }}>
            <button
              className="addquestionitem bt orange"
              onClick={(e) => {
                e.preventDefault()
                setQuestions((current) => [...current, ''])
              }}
            >
              Добавить вопрос
            </button>
          </div>
        </div>
      </form>
    </>
  )
}
